/* eslint-disable no-unused-expressions */

/*
  example usage:
   <input-wrapper class="input-wrapper">
      <label for="Email--{{ unique }}" class="input-wrapper__label">Email</label>
      <input
        type="email"
        id="Email--{{ unique }}"
        class="input-wrapper__field {% if form.errors %}input-wrapper__field--error{% endif %}"
        placeholder="Email"
        name="contact[email]"
      >
    </input-wrapper>
*/

class InputWrapper extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input') || this.querySelector('select') || this.querySelector('textarea');
    this.label = this.querySelector('label');

    if (!this.input || !this.label) {
      throw new Error('<input-wrapper></input-wrapper> must have an input and label');
    }
    window.addEventListener('load', () => {
      const mustBeActive = this.input.value || this.input.classList.contains('input-wrapper__field--active');
      mustBeActive ? this.activate() : this.deactivate(true);
    });

    this.setEventListeners();
  }

  setEventListeners() {
    // if input press enter and input has value checkError
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && this.input.value) {
        this.checkIfInputValid();
      }
    });
    this.input.addEventListener('focus', this.removeError.bind(this));

    this.input.addEventListener('input', () => {
      this.removeError();
      this.input.value && this.activate();
    });

    this.input.addEventListener('blur', () => {
      if (!this.input.value) {
        this.deactivate();
        this.removeError();
      } else {
        this.checkIfInputValid();
      }
    });

    this.input.addEventListener('change', () => {
      if (!this.input.value) {
        this.deactivate();
        this.removeError();
      } else {
        this.checkIfInputValid();
      }
    });

    if (this.classList.contains('input-wrapper__password')) {
      this.validatePassword(this.input);
    }

    if (this.classList.contains('input-wrapper__password-confirm')) {
      this.validateConfirmPassword(this.input);
    }
  }

  setError() {
    this.input.classList.add('input-wrapper__field--error');
    this.closest('[data-input-wrapper-container]')?.classList.add('input-wrapper-container--error');
    this.classList.contains('input-wrapper__password-confirm') && this.classList.add('error');
    this.classList.contains('input-wrapper__password') && this.classList.add('error');
    this.querySelector('.validation-error')?.classList.remove('hidden');
    this.querySelector('.confirm-password-error')?.classList.remove('hidden');
  }

  removeError() {
    this.input.classList.remove('input-wrapper__field--error');
    this.closest('[data-input-wrapper-container]')?.classList.remove('input-wrapper-container--error');
    this.classList.contains('input-wrapper__password-confirm') && this.classList.remove('error');
    this.classList.contains('input-wrapper__password') && this.classList.remove('error');
    this.querySelector('.validation-error')?.classList.add('hidden');
    this.querySelector('.confirm-password-error')?.classList.add('hidden');
  }

  checkIfInputValid() {
    this.input.value && this.input.matches(':invalid') ? this.setError() : this.removeError();
  }

  activate() {
    this.classList.add('input-wrapper--filled');
    this.classList.remove('input-wrapper--empty');
  }

  deactivate(isFirstRender = false) {
    this.classList.remove('input-wrapper--filled');
    !isFirstRender && this.classList.add('input-wrapper--empty');
  }

  static isValidated(inputEl) {
    const validationStates = {
      symbol: false,
      UpperCase: false,
      LowerCase: false,
      number: false,
      length: false,
    };
    validationStates.symbol = /[^a-zA-Z0-9]/.test(inputEl.value);
    validationStates.UpperCase = /[A-Z]/.test(inputEl.value);
    validationStates.LowerCase = /[a-z]/.test(inputEl.value);
    validationStates.number = /[0-9]/.test(inputEl.value);
    validationStates.length = inputEl.value.length >= 8;

    const isValid = Object.values(validationStates).every((value) => value);
    return isValid;
  }

  validatePassword(inputEl) {
    inputEl.addEventListener('blur', (event) => {
      if (!event.target.value) return;
      const isValid = InputWrapper.isValidated(inputEl);
      if (!isValid) {
        this.setError();
      } else {
        this.removeError();
      }
    });
  }

  validateConfirmPassword(inputEl) {
    const formEl = this.closest('form');
    const passwordInputEl = formEl.querySelector('.input-wrapper__password input');
    inputEl.addEventListener('blur', (event) => {
      if (!event.target.value) return;
      if (event.target.value !== passwordInputEl.value) {
        this.setError();
      } else {
        this.removeError();
      }
    });
  }
}

customElements.define('input-wrapper', InputWrapper);
